import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Table,
  Tooltip,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Card,
  Drawer,
  Tabs,
  Tag,
  Space,
  Checkbox,
  InputNumber,
  DatePicker,
  Divider,
  Descriptions,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

import {
  save_cleaning_type_for_line,
  update_cleaning_type,
  get_cleaning_type_for_line,
  delete_cleaning_type,
} from '../../../utils/api';

import Icons from '@/components/third-party/Icons';
import ReusableSearchInput from '@/components/ReusableSearch';
import { useTranslation } from 'react-i18next';
import StatCard from '@/components/StatCard';
import { Sparkles, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { Typography } from 'antd';
import { formatDate } from '@/utils/datetime';

const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

// ✅ Color-coded status maps
const riskLevelColors = {
  LOW: 'green',
  MEDIUM: 'orange',
  HIGH: 'red',
  CRITICAL: 'purple',
};

const validationStatusColors = {
  NOT_VALIDATED: 'default',
  IN_PROGRESS: 'processing',
  VALIDATED: 'success',
  REVALIDATION_DUE: 'warning',
};

const CleaningTypes = ({ show_config }) => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [searchvalue, setSearchvalue] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [CleaningData, setCleaningData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const { t } = useTranslation();

  // ✅ API success handlers
  const successCleaningList = (res) => {
    if (res?.data?.status === 'failed') {
      toast.warning(res?.data?.message);
      return;
    }
    setCleaningData(res?.data?.data || []);
  };

  // ✅ Fetch data
  const getAllData = async () => {
    try {
      await get_cleaning_type_for_line(successCleaningList);
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    setShowForm(false);
  }, [show_config]);

  // ✅ Add or update Cleaning
  const handleAdd = async (values, resetForm) => {
    try {
      if (isEdit) {
        // Use UPDATE API when editing
        await update_cleaning_type(values, handleSuccess(resetForm));
      } else {
        // Use ADD API when creating
        await save_cleaning_type_for_line(values, handleSuccess(resetForm));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSuccess = (resetForm) => (res) => {
    if (res.status === 'failed') {
      toast.warning(res.message);
    } else {
      toast.success(res.message);
      getAllData();
      setShowForm(false);
      setIsEdit(false);
      setEditData(null);
      if (resetForm) resetForm();
    }
  };

  // ✅ Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  // ✅ Handle edit
  const handleEdit = (record) => {
    setIsEdit(true);
    setEditData(record);
    setShowForm(true);
  };

  // ✅ Handle delete
  const handleDelete = async (record) => {
    try {
      await delete_cleaning_type({ id: record?.id }, (res) => {
        if (res?.status === 'success') {
          toast.success(res?.message || 'Cleaning type deleted successfully');
          getAllData();
        } else {
          toast.warning(res?.message || 'Failed to delete cleaning type');
        }
      });
    } catch (error) {
      console.error('Error deleting cleaning type:', error);
      toast.error('Failed to delete cleaning type');
    }
  };

  // Enhanced table columns with useTableConfig properties
  const allColumns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      defaultVisible: false,
      sortable: true,
      sortType: 'number',
      width: 80,
      align: 'center',
    },
    {
      key: 'cleaning_code',
      title: 'Code',
      dataIndex: 'cleaning_code',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 100,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'name',
      title: 'Cleaning Type',
      dataIndex: 'name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 200,
      align: 'left',
    },
    {
      key: 'cleaning_category',
      title: 'Category',
      dataIndex: 'cleaning_category_display',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'cleaning_method',
      title: 'Method',
      dataIndex: 'cleaning_method_display',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (text) => text || '-',
    },
    {
      key: 'duration',
      title: 'Duration',
      dataIndex: 'estimated_duration_minutes',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
      width: 100,
      align: 'center',
      render: (mins) => (mins ? `${mins} min` : '-'),
    },
    {
      key: 'risk_level',
      title: 'Risk Level',
      dataIndex: 'risk_level',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 120,
      align: 'center',
      render: (risk, record) =>
        risk ? <Tag color={riskLevelColors[risk]}>{record.risk_level_display}</Tag> : '-',
    },
    {
      key: 'validation_status',
      title: 'Validation Status',
      dataIndex: 'validation_status',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 150,
      align: 'center',
      render: (status, record) =>
        status ? (
          <Tag color={validationStatusColors[status]}>{record.validation_status_display}</Tag>
        ) : (
          '-'
        ),
    },
    {
      key: 'approvals',
      title: 'Approvals Required',
      dataIndex: 'approvals',
      defaultVisible: false,
      sortable: false,
      width: 150,
      align: 'center',
      render: (_, record) => {
        const approvals = [];
        if (record.requires_supervisor_approval) approvals.push('Supervisor');
        if (record.requires_qa_approval) approvals.push('QA');
        return approvals.length > 0 ? (
          <Space size={4} wrap>
            {approvals.map((a) => (
              <Tag key={a} color="blue" style={{ fontSize: '11px' }}>
                {a}
              </Tag>
            ))}
          </Space>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'is_active',
      title: 'Status',
      dataIndex: 'is_active',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: 100,
      align: 'center',
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      key: 'created_at',
      title: 'Created Date',
      dataIndex: 'created_at',
      defaultVisible: false,
      sortable: true,
      sortType: 'date',
      width: 150,
      align: 'center',
      render: (date) => (date ? formatDate(date) : '-'),
    },
    {
      key: 'updated_at',
      title: 'Last Updated',
      dataIndex: 'updated_at',
      defaultVisible: false,
      sortable: true,
      sortType: 'date',
      width: 150,
      align: 'center',
      render: (date) => (date ? formatDate(date) : '-'),
    },
    {
      key: 'actions',
      title: 'Actions',
      dataIndex: 'actions',
      defaultVisible: true,
      alwaysVisible: true,
      sortable: false,
      width: 120,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip title="Edit">
            <Icons.Pencil
              onClick={() => handleEdit(record)}
              className="text-amber-600 w-5 h-5 cursor-pointer hover:text-amber-800"
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Icons.Eye
              onClick={() => handleViewDetails(record)}
              className="text-blue-600 w-5 h-5 cursor-pointer hover:text-blue-800"
            />
          </Tooltip>
          <Popconfirm
            title={`Are you sure you want to delete "${record?.name}"?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Icons.Trash2 className="text-red-600 w-5 h-5 cursor-pointer hover:text-red-800" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // ✅ Validation schema (basic fields only for now)
  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Cleaning name is required'),
    description: Yup.string().nullable(),
    cleaning_code: Yup.string().nullable(),
    cleaning_category: Yup.string().nullable(),
    cleaning_method: Yup.string().nullable(),
    estimated_duration_minutes: Yup.number().nullable().min(0),
    risk_level: Yup.string().nullable(),
  });

  // Use table configuration hook
  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'cleaningTypesTable',
    columns: allColumns,
    data: CleaningData,
    searchValue: searchvalue,
  });

  // ✅ Render detail drawer with comprehensive tabs
  const renderDetailDrawer = () => {
    if (!selectedRecord) return null;

    const items = [
      {
        key: '1',
        label: 'Basic Information',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Cleaning Code" span={2}>
              {selectedRecord.cleaning_code || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Name" span={2}>
              {selectedRecord.name}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {selectedRecord.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Category">
              {selectedRecord.cleaning_category_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              {selectedRecord.cleaning_method_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedRecord.is_active ? 'green' : 'default'}>
                {selectedRecord.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="cGMP Critical">
              {selectedRecord.cgmp_critical ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '2',
        label: 'Frequency & Timing',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Frequency Requirement" span={2}>
              {selectedRecord.frequency_requirement || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Estimated Duration">
              {selectedRecord.estimated_duration_minutes
                ? `${selectedRecord.estimated_duration_minutes} minutes`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Drying Time">
              {selectedRecord.drying_time_minutes
                ? `${selectedRecord.drying_time_minutes} minutes`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Hold Time" span={2}>
              {selectedRecord.hold_time_hours ? `${selectedRecord.hold_time_hours} hours` : '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '3',
        label: 'Materials & Agents',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Cleaning Agents" span={2}>
              {selectedRecord.cleaning_agents || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Detergent Type">
              {selectedRecord.detergent_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sanitizer Type">
              {selectedRecord.sanitizer_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Rinse Agent">
              {selectedRecord.rinse_agent || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Water Quality">
              {selectedRecord.water_quality_display || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '4',
        label: 'Equipment & Personnel',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Equipment/Tools Required" span={2}>
              {selectedRecord.equipment_tools_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="PPE Requirements" span={2}>
              {selectedRecord.ppe_requirements || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Minimum Personnel">
              {selectedRecord.minimum_personnel_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Qualification Required">
              {selectedRecord.operator_qualification_required || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Training Required">
              {selectedRecord.training_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Training Reference">
              {selectedRecord.training_reference || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '5',
        label: 'Verification & Testing',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Verification Method" span={2}>
              {selectedRecord.verification_method_display || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Acceptance Criteria" span={2}>
              {selectedRecord.acceptance_criteria || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Visual Inspection">
              {selectedRecord.visual_inspection_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Swab Test">
              {selectedRecord.swab_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Rinse Test">
              {selectedRecord.rinse_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Microbial Test">
              {selectedRecord.microbial_test_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Residue Limit" span={2}>
              {selectedRecord.residue_limit_ppm ? `${selectedRecord.residue_limit_ppm} ppm` : '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '6',
        label: 'Documentation',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="SOP Reference">
              {selectedRecord.sop_reference || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Checklist Required">
              {selectedRecord.checklist_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Photographic Evidence">
              {selectedRecord.photographic_evidence_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Batch Record Entry">
              {selectedRecord.batch_record_entry_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '7',
        label: 'Validation & Compliance',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Validation Required">
              {selectedRecord.validation_required ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Validation Status">
              <Tag color={validationStatusColors[selectedRecord.validation_status]}>
                {selectedRecord.validation_status_display || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Protocol Reference" span={2}>
              {selectedRecord.validation_protocol_reference || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Last Validation Date">
              {selectedRecord.last_validation_date || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Next Validation Date">
              {selectedRecord.next_validation_date || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="cGMP Critical">
              {selectedRecord.cgmp_critical ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Regulatory Requirement" span={2}>
              {selectedRecord.regulatory_requirement || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '8',
        label: 'Risk & Safety',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Risk Level" span={2}>
              <Tag color={riskLevelColors[selectedRecord.risk_level]}>
                {selectedRecord.risk_level_display || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Safety Precautions" span={2}>
              {selectedRecord.safety_precautions || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Environmental Controls" span={2}>
              {selectedRecord.environmental_controls_required ? 'Required' : 'Not Required'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '9',
        label: 'Applicability',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Applicable Equipment" span={2}>
              {selectedRecord.applicable_equipment || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Applicable Areas" span={2}>
              {selectedRecord.applicable_areas || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Product Contact Surfaces Only" span={2}>
              {selectedRecord.product_contact_surfaces_only ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '10',
        label: 'Approvals & Quality',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Supervisor Approval">
              {selectedRecord.requires_supervisor_approval ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="QA Approval">
              {selectedRecord.requires_qa_approval ? 'Required' : 'Not Required'}
            </Descriptions.Item>
            <Descriptions.Item label="Critical to Quality">
              {selectedRecord.critical_to_quality ? 'Yes' : 'No'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: '11',
        label: 'Audit Trail',
        children: (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Version Number">
              {selectedRecord.version_number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created By">
              {selectedRecord.created_by || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created On">
              {selectedRecord.created_on || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Modified By">
              {selectedRecord.modified_by || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Modified On" span={2}>
              {selectedRecord.modified_on || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedRecord.notes || '-'}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
    ];

    return (
      <Drawer
        title={`Cleaning Type Details: ${selectedRecord.name}`}
        placement="right"
        width={800}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Tabs items={items} />
      </Drawer>
    );
  };

  // ✅ Enhanced form with comprehensive fields
  const renderForm = () => (
    <div
      className="rounded-xl border mb-5"
      style={{
        backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f8faff',
        borderColor: isEdit ? '#f59e0b' : '#3b82f6',
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        padding: '20px 24px 12px',
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div
          className="p-1.5 rounded-md"
          style={{
            backgroundColor: isEdit
              ? isDark
                ? 'rgba(245,158,11,0.08)'
                : '#fffbeb'
              : isDark
              ? 'rgba(59,130,246,0.08)'
              : '#eff6ff',
          }}
        >
          {isEdit ? (
            <Icons.Pencil className="w-4 h-4" style={{ color: '#f59e0b' }} />
          ) : (
            <Icons.Plus className="w-4 h-4" style={{ color: '#3b82f6' }} />
          )}
        </div>
        <span className="text-sm font-semibold" style={{ color: isEdit ? '#f59e0b' : '#3b82f6' }}>
          {isEdit ? 'Edit Cleaning Type' : 'Add New Cleaning Type'}
        </span>
        {isEdit && (
          <Tag color="warning" className="ml-2 text-xs">
            Editing
          </Tag>
        )}
      </div>
      <Formik
        enableReinitialize
        initialValues={
          isEdit && editData
            ? {
                id: editData?.id || null,
                name: editData?.name || '',
                description: editData?.description || '',
                cleaning_code: editData?.cleaning_code || '',
                cleaning_category: editData?.cleaning_category || '',
                cleaning_method: editData?.cleaning_method || '',
                estimated_duration_minutes: editData?.estimated_duration_minutes || null,
                drying_time_minutes: editData?.drying_time_minutes || null,
                hold_time_hours: editData?.hold_time_hours || null,
                frequency_requirement: editData?.frequency_requirement || '',
                cleaning_agents: editData?.cleaning_agents || '',
                detergent_type: editData?.detergent_type || '',
                sanitizer_type: editData?.sanitizer_type || '',
                rinse_agent: editData?.rinse_agent || '',
                water_quality_required: editData?.water_quality_required || '',
                equipment_tools_required: editData?.equipment_tools_required || '',
                ppe_requirements: editData?.ppe_requirements || '',
                minimum_personnel_required: editData?.minimum_personnel_required || 1,
                operator_qualification_required: editData?.operator_qualification_required || '',
                training_required:
                  editData?.training_required !== undefined ? editData.training_required : true,
                training_reference: editData?.training_reference || '',
                verification_method: editData?.verification_method || '',
                acceptance_criteria: editData?.acceptance_criteria || '',
                visual_inspection_required:
                  editData?.visual_inspection_required !== undefined
                    ? editData.visual_inspection_required
                    : true,
                swab_test_required: editData?.swab_test_required || false,
                rinse_test_required: editData?.rinse_test_required || false,
                microbial_test_required: editData?.microbial_test_required || false,
                residue_limit_ppm: editData?.residue_limit_ppm || null,
                sop_reference: editData?.sop_reference || '',
                checklist_required:
                  editData?.checklist_required !== undefined ? editData.checklist_required : true,
                photographic_evidence_required: editData?.photographic_evidence_required || false,
                batch_record_entry_required:
                  editData?.batch_record_entry_required !== undefined
                    ? editData.batch_record_entry_required
                    : true,
                validation_required:
                  editData?.validation_required !== undefined ? editData.validation_required : true,
                validation_protocol_reference: editData?.validation_protocol_reference || '',
                validation_status: editData?.validation_status || 'NOT_VALIDATED',
                cgmp_critical:
                  editData?.cgmp_critical !== undefined ? editData.cgmp_critical : true,
                regulatory_requirement: editData?.regulatory_requirement || '',
                risk_level: editData?.risk_level || '',
                safety_precautions: editData?.safety_precautions || '',
                environmental_controls_required: editData?.environmental_controls_required || false,
                applicable_equipment: editData?.applicable_equipment || '',
                applicable_areas: editData?.applicable_areas || '',
                product_contact_surfaces_only: editData?.product_contact_surfaces_only || false,
                is_active: editData?.is_active !== undefined ? editData.is_active : true,
                requires_supervisor_approval: editData?.requires_supervisor_approval || false,
                requires_qa_approval: editData?.requires_qa_approval || false,
                critical_to_quality: editData?.critical_to_quality || false,
                notes: editData?.notes || '',
              }
            : {
                name: '',
                description: '',
                cleaning_code: '',
                cleaning_category: '',
                cleaning_method: '',
                estimated_duration_minutes: null,
                drying_time_minutes: null,
                hold_time_hours: null,
                frequency_requirement: '',
                cleaning_agents: '',
                detergent_type: '',
                sanitizer_type: '',
                rinse_agent: '',
                water_quality_required: '',
                equipment_tools_required: '',
                ppe_requirements: '',
                minimum_personnel_required: 1,
                operator_qualification_required: '',
                training_required: true,
                training_reference: '',
                verification_method: '',
                acceptance_criteria: '',
                visual_inspection_required: true,
                swab_test_required: false,
                rinse_test_required: false,
                microbial_test_required: false,
                residue_limit_ppm: null,
                sop_reference: '',
                checklist_required: true,
                photographic_evidence_required: false,
                batch_record_entry_required: true,
                validation_required: true,
                validation_protocol_reference: '',
                validation_status: 'NOT_VALIDATED',
                cgmp_critical: true,
                regulatory_requirement: '',
                risk_level: '',
                safety_precautions: '',
                environmental_controls_required: false,
                applicable_equipment: '',
                applicable_areas: '',
                product_contact_surfaces_only: false,
                is_active: true,
                requires_supervisor_approval: false,
                requires_qa_approval: false,
                critical_to_quality: false,
                notes: '',
              }
        }
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => handleAdd(values, resetForm)}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, errors, touched, handleReset }) => (
          <Form layout="vertical" onFinish={handleSubmit}>
            {/* Basic Information */}
            <Divider orientation="left">Basic Information</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Cleaning Type Name"
                  validateStatus={touched.name && errors.name ? 'error' : ''}
                  help={touched.name && errors.name ? errors.name : ''}
                  required
                >
                  <Input
                    size="large"
                    name="name"
                    placeholder="Enter Cleaning Type Name"
                    value={values.name}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Cleaning Code">
                  <Input
                    size="large"
                    name="cleaning_code"
                    placeholder="e.g., CLN-001"
                    value={values.cleaning_code}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Category">
                  <Select
                    size="large"
                    placeholder="Select Category"
                    value={values.cleaning_category}
                    onChange={(val) => setFieldValue('cleaning_category', val)}
                    allowClear
                  >
                    <Option value="ROUTINE">Routine Cleaning</Option>
                    <Option value="CHANGEOVER">Product Changeover</Option>
                    <Option value="CAMPAIGN">Campaign Cleaning</Option>
                    <Option value="DEEP">Deep/Terminal Cleaning</Option>
                    <Option value="EMERGENCY">Emergency/Breakdown</Option>
                    <Option value="SANITIZATION">Sanitization</Option>
                    <Option value="STERILIZATION">Sterilization</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Description">
                  <TextArea
                    size="large"
                    name="description"
                    placeholder="Enter detailed description"
                    value={values.description}
                    onChange={handleChange}
                    rows={3}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Classification & Timing */}
            <Divider orientation="left">Method & Timing</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Cleaning Method">
                  <Select
                    size="large"
                    placeholder="Select Method"
                    value={values.cleaning_method}
                    onChange={(val) => setFieldValue('cleaning_method', val)}
                    allowClear
                  >
                    <Option value="DRY">Dry Cleaning</Option>
                    <Option value="WET">Wet Cleaning</Option>
                    <Option value="DRY_WET">Dry + Wet Cleaning</Option>
                    <Option value="CIP">Clean-In-Place (CIP)</Option>
                    <Option value="MANUAL">Manual Cleaning</Option>
                    <Option value="STEAM">Steam Cleaning</Option>
                    <Option value="AUTOMATED">Automated Cleaning</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Duration (minutes)">
                  <InputNumber
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="Minutes"
                    value={values.estimated_duration_minutes}
                    onChange={(val) => setFieldValue('estimated_duration_minutes', val)}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Drying Time (minutes)">
                  <InputNumber
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="Minutes"
                    value={values.drying_time_minutes}
                    onChange={(val) => setFieldValue('drying_time_minutes', val)}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Hold Time (hours)">
                  <InputNumber
                    size="large"
                    style={{ width: '100%' }}
                    placeholder="Hours"
                    value={values.hold_time_hours}
                    onChange={(val) => setFieldValue('hold_time_hours', val)}
                    min={0}
                    step={0.5}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Frequency Requirement">
                  <Input
                    size="large"
                    name="frequency_requirement"
                    placeholder="e.g., After each batch, Daily, Weekly"
                    value={values.frequency_requirement}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Verification & Quality */}
            <Divider orientation="left">Verification & Quality</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Verification Method">
                  <Select
                    size="large"
                    placeholder="Select Method"
                    value={values.verification_method}
                    onChange={(val) => setFieldValue('verification_method', val)}
                    allowClear
                  >
                    <Option value="VISUAL">Visual Inspection</Option>
                    <Option value="SWAB">Swab Test</Option>
                    <Option value="RINSE">Rinse Test</Option>
                    <Option value="TOC">TOC Analysis</Option>
                    <Option value="MICROBIAL">Microbial Test</Option>
                    <Option value="COMBINATION">Combination of Methods</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Water Quality Required">
                  <Select
                    size="large"
                    placeholder="Select Water Quality"
                    value={values.water_quality_required}
                    onChange={(val) => setFieldValue('water_quality_required', val)}
                    allowClear
                  >
                    <Option value="POTABLE">Potable Water</Option>
                    <Option value="PURIFIED">Purified Water</Option>
                    <Option value="WFI">Water For Injection (WFI)</Option>
                    <Option value="DEIONIZED">Deionized Water</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Acceptance Criteria">
                  <Input
                    size="large"
                    name="acceptance_criteria"
                    placeholder="e.g., No visible residue, <10 ppm limit"
                    value={values.acceptance_criteria}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Risk & Compliance */}
            <Divider orientation="left">Risk & Compliance</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Risk Level">
                  <Select
                    size="large"
                    placeholder="Select Risk Level"
                    value={values.risk_level}
                    onChange={(val) => setFieldValue('risk_level', val)}
                    allowClear
                  >
                    <Option value="LOW">Low Risk</Option>
                    <Option value="MEDIUM">Medium Risk</Option>
                    <Option value="HIGH">High Risk</Option>
                    <Option value="CRITICAL">Critical Risk</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Validation Status">
                  <Select
                    size="large"
                    placeholder="Select Status"
                    value={values.validation_status}
                    onChange={(val) => setFieldValue('validation_status', val)}
                  >
                    <Option value="NOT_VALIDATED">Not Validated</Option>
                    <Option value="IN_PROGRESS">Validation In Progress</Option>
                    <Option value="VALIDATED">Validated</Option>
                    <Option value="REVALIDATION_DUE">Revalidation Due</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="SOP Reference">
                  <Input
                    size="large"
                    name="sop_reference"
                    placeholder="e.g., SOP-CLN-001"
                    value={values.sop_reference}
                    onChange={handleChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Status">
                  <Select
                    size="large"
                    value={values.is_active}
                    onChange={(val) => setFieldValue('is_active', val)}
                  >
                    <Option value={true}>Active</Option>
                    <Option value={false}>Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Checkboxes */}
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.cgmp_critical}
                    onChange={(e) => setFieldValue('cgmp_critical', e.target.checked)}
                  >
                    cGMP Critical
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.requires_supervisor_approval}
                    onChange={(e) =>
                      setFieldValue('requires_supervisor_approval', e.target.checked)
                    }
                  >
                    Supervisor Approval Required
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.requires_qa_approval}
                    onChange={(e) => setFieldValue('requires_qa_approval', e.target.checked)}
                  >
                    QA Approval Required
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Checkbox
                    checked={values.checklist_required}
                    onChange={(e) => setFieldValue('checklist_required', e.target.checked)}
                  >
                    Checklist Required (BMR)
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {/* Submit Buttons */}
            <Row>
              <Col span={24} className="flex items-end justify-end gap-2">
                <Tooltip title="Reset">
                  <Button
                    icon={<Icons.Undo2 className="w-4 h-4" />}
                    onClick={() => {
                      handleReset();
                      setShowForm(false);
                      setIsEdit(false);
                      setEditData(null);
                    }}
                  />
                </Tooltip>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    isEdit ? (
                      <Icons.Check className="w-4 h-4" />
                    ) : (
                      <Icons.Plus className="w-4 h-4" />
                    )
                  }
                  style={isEdit ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } : {}}
                >
                  {isEdit ? 'Update' : 'Add'}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Sparkles className="w-7 h-7 text-blue-600" />
            <Title level={4} style={{ color: textColor, margin: 0 }}>
              {t('Cleaning Types Configuration')}
            </Title>
          </div>
        }
        extra={
          <div className="flex items-center gap-2">
            <Tooltip title={showForm ? 'Close Form' : 'Add Cleaning Type'} placement="bottom">
              {showForm ? (
                <Icons.X
                  className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer text-red-500"
                  onClick={() => {
                    setShowForm(false);
                    setIsEdit(false);
                    setEditData(null);
                  }}
                />
              ) : (
                <Icons.Plus
                  className="w-7 h-7 hover:scale-110 transition-transform cursor-pointer text-black"
                  onClick={() => {
                    setShowForm(true);
                    setIsEdit(false);
                    setEditData(null);
                  }}
                />
              )}
            </Tooltip>
            <TableColumnConfig menu={columnConfigMenu} />
            <ReusableSearchInput
              placeholder="Search cleaning types..."
              allowClear
              style={{ width: 300 }}
              value={searchvalue}
              onChange={(e) => setSearchvalue(e.target.value)}
              onSearch={(val) => setSearchvalue(val)}
            />
          </div>
        }
      >
        {/* Top Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Total Cleaning Types"
              value={CleaningData?.length || 0}
              icon={<Sparkles size={24} />}
              bgColor="#dbeafe"
              iconColor="#3b82f6"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Validated Types"
              value={CleaningData?.filter((c) => c?.validation_status === 'VALIDATED')?.length || 0}
              icon={<CheckCircle size={24} />}
              bgColor="#d1fae5"
              iconColor="#10b981"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="High Risk"
              value={
                CleaningData?.filter(
                  (c) => c?.risk_level === 'HIGH' || c?.risk_level === 'CRITICAL'
                )?.length || 0
              }
              icon={<AlertTriangle size={24} />}
              bgColor="#fee2e2"
              iconColor="#ef4444"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              label="Revalidation Due"
              value={
                CleaningData?.filter((c) => c?.validation_status === 'REVALIDATION_DUE')?.length ||
                0
              }
              icon={<Clock size={24} />}
              bgColor="#fef9c3"
              iconColor="#facc15"
            />
          </Col>
        </Row>

        {showForm && renderForm()}

        <Table
          bordered
          size="small"
          dataSource={filteredData}
          columns={visibleColumns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} cleaning types`,
          }}
          scroll={{ x: 'max-content' }}
        />

        {renderDetailDrawer()}
      </Card>
    </div>
  );
};

export default CleaningTypes;
